import { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Globe } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSChrome, setIsIOSChrome] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Vérifier si déjà installé en mode standalone
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    if (standalone) {
      return; // Ne pas afficher si déjà installé
    }

    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Détecter Chrome sur iOS (CriOS)
    const iOSChrome = iOS && /CriOS/.test(navigator.userAgent);
    setIsIOSChrome(iOSChrome);

    // Vérifier si l'utilisateur a déjà refusé récemment (24h)
    const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      const oneDay = 24 * 60 * 60 * 1000;
      if (now - dismissedTime < oneDay) {
        return; // Ne pas afficher si refusé il y a moins de 24h
      }
    }

    // Pour iOS, afficher après un délai
    if (iOS) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // 3 secondes après le chargement
      return () => clearTimeout(timer);
    }

    // Pour Android/Chrome, écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Afficher la bannière après un court délai
      setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    // Afficher le prompt d'installation natif
    deferredPrompt.prompt();
    
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installée avec succès');
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  // Ne rien afficher si déjà installé ou si la bannière ne doit pas être montrée
  if (isStandalone || !showPrompt) {
    return null;
  }

  // Bannière pour iOS (instructions manuelles)
  if (isIOS) {
    return (
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up"
        style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <div className="px-4 pb-2">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 max-w-md mx-auto">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <img 
                  src="/logo-icon.png" 
                  alt="CabGolf" 
                  className="w-12 h-12 rounded-xl"
                />
                <div>
                  <h3 className="font-bold text-gray-900 text-base">Installer CabGolf</h3>
                  <p className="text-xs text-gray-500">Accès rapide depuis l'écran d'accueil</p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Instructions iOS */}
            <div className="bg-gray-50 rounded-xl p-3 mb-3">
              <p className="text-sm text-gray-700 mb-2 font-medium">Pour installer :</p>
              <ol className="text-sm text-gray-600 space-y-2">
                {/* Étape 0 si Chrome sur iOS */}
                {isIOSChrome && (
                  <li className="flex items-start gap-2">
                    <span className="flex items-center justify-center w-5 h-5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                    <span>
                      <span className="font-medium text-orange-700">Ouvrez cette page dans Safari</span>
                      <span className="text-gray-500 text-xs block">(Chrome ne supporte pas l'installation)</span>
                    </span>
                  </li>
                )}
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 bg-green-100 text-green-700 rounded-full text-xs font-bold flex-shrink-0">{isIOSChrome ? '2' : '1'}</span>
                  <span>Appuyez sur</span>
                  <Share size={16} className="text-blue-500 flex-shrink-0" />
                  <span className="text-gray-500 text-xs">(en bas)</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="flex items-center justify-center w-5 h-5 bg-green-100 text-green-700 rounded-full text-xs font-bold flex-shrink-0">{isIOSChrome ? '3' : '2'}</span>
                  <span>"Sur l'écran d'accueil"</span>
                  <Plus size={16} className="text-gray-600 flex-shrink-0" />
                </li>
              </ol>
            </div>
            
            {/* Bouton de fermeture */}
            <button
              onClick={handleDismiss}
              className="w-full py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 rounded-xl"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Bannière pour Android/Chrome (bouton d'installation)
  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up"
      style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
    >
      <div className="px-4 pb-2">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 max-w-md mx-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-cabgolf.png" 
                alt="CabGolf" 
                className="w-12 h-12 rounded-xl"
              />
              <div>
                <h3 className="font-bold text-gray-900 text-base">Installer CabGolf</h3>
                <p className="text-xs text-gray-500">Accès rapide depuis l'écran d'accueil</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fermer"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Avantages */}
          <div className="bg-green-50 rounded-xl p-3 mb-3">
            <ul className="text-sm text-green-800 space-y-1">
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span> Ouvrir en plein écran
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span> Accès en un clic
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600">✓</span> Expérience comme une app native
              </li>
            </ul>
          </div>
          
          {/* Boutons */}
          <div className="flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 rounded-xl"
            >
              Plus tard
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 transition-colors rounded-xl flex items-center justify-center gap-2"
            >
              <Download size={16} />
              Installer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
